import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import { FaSyncAlt, FaChevronDown, FaChevronRight, FaCopy, FaCheck } from 'react-icons/fa';
import apiService from '../services/api';
import { isSuperHit } from '../utils/superHit';
import { scannerFetch } from '../utils/scannerFetch';
import './MyHits.css';

interface Hit {
  id: string;
  url: string;
  path: string;
  discoveredAt: string;
  services: string[];
  credentials: any;
  status: 'valid' | 'invalid';
  isNew: boolean;
  verificationResult?: any;
  duplicateCount?: number;
  _sourceVps?: { ip: string; port: string };
}

interface MyHitsProps {
  vpsConfig: { ip: string; port: string };
  vpsList?: { ip: string; port: string }[];
  setActiveTab?: (tab: string) => void;
}

const PAGE_SIZE = 20;

type DisplayStatus = 'valide' | 'brut' | 'invalide';

function getDisplayStatus(hit: Hit): DisplayStatus {
  if (hit.status === 'invalid') {
    if (isBrutInvalid(hit)) return 'brut';
    return 'invalide';
  }
  if (hit.status === 'valid' && !hit.verificationResult) return 'brut';
  return 'valide';
}

const STATUS_LABEL: Record<DisplayStatus, string> = {
  valide:   'Valide',
  brut:     'Brut',
  invalide: 'Invalide',
};

/* ─── AWS RANDOM detection ───────────────────────────────── */
const AKIA_RE = /^AKIA[A-Z0-9]{12,}/;

function isAwsRandom(creds: any): boolean {
  if (!creds) return false;
  const user = creds.username || creds.user || creds.email || '';
  return typeof user === 'string' && AKIA_RE.test(user.trim());
}

// Retourne le nom d'affichage effectif d'un service (remplace smtp → AWS_RANDOM si AKIA)
function getEffectiveSvcName(serviceKey: string, creds: any): string {
  const k = serviceKey.toLowerCase();
  if ((k === 'smtp' || k === 'mail' || k === 'mailer') && isAwsRandom(creds)) {
    return 'AWS_RANDOM';
  }
  return serviceKey.toUpperCase();
}

// Chips à afficher dans la ligne de tableau pour un hit
function getDisplaySvcChips(hit: Hit): Array<{ key: string; label: string; awsRandom: boolean }> {
  return (hit.services ?? []).map(s => {
    const creds = hit.credentials?.[s] ?? hit.credentials?.[s.toLowerCase()];
    const label = getEffectiveSvcName(s, creds ?? hit.credentials);
    return { key: s, label, awsRandom: label === 'AWS_RANDOM' };
  });
}

/* ─── Invalides "bruts" : réseau / auth / format ────────── */
function isBrutInvalid(hit: Hit): boolean {
  if (hit.status !== 'invalid') return false;
  const vr = hit.verificationResult;
  if (!vr) return false;
  const str = typeof vr === 'string' ? vr : JSON.stringify(vr);
  return /timeout|cancel|bad.?request|400|unauthorized|401|rate.?limit|429|refused|econnrefused|enotfound|network.?error/i.test(str);
}

/* ─── XP par hit ─────────────────────────────────────────── */
function calcHitXP(hit: Hit): number {
  let xp = 0;
  if (hit.status === 'valid') {
    xp += 50;
    xp += (hit.services?.length || 0) * 30;
    if (hit.credentials) {
      const len = JSON.stringify(hit.credentials).length;
      xp += len > 200 ? 80 : len > 50 ? 35 : 10;
    }
    if (hit.verificationResult) xp += 120;
  } else {
    xp += 5;
  }
  const id = hit.id || '';
  if (id.length >= 2) {
    xp += (id.charCodeAt(id.length - 1) % 20) + (id.charCodeAt(id.length - 2) % 12);
  }
  return xp;
}

/* ─── Credential value (VALEUR column) — service-aware ───── */
function extractCredValue(hit: Hit): string {
  if (!hit.credentials) return '—';
  const svcs = (hit.services ?? []).map(s => s.toLowerCase());

  // AWS → show AKIA
  if (svcs.some(s => s === 'aws' || s === 'amazon')) {
    const awsCreds = hit.credentials.aws || hit.credentials.amazon || hit.credentials;
    const akia = awsCreds.akia || awsCreds.accessKeyId || awsCreds.access_key;
    if (akia) return akia;
  }

  // SMTP → si AWS RANDOM, show AKIA ; sinon show host
  if (svcs.some(s => s === 'smtp' || s === 'mail' || s === 'mailer')) {
    const smtpCreds = hit.credentials.smtp || hit.credentials.mail || hit.credentials;
    if (isAwsRandom(smtpCreds)) {
      const akia = smtpCreds.username || smtpCreds.user;
      if (akia) return akia;
    }
    if (smtpCreds.host) return smtpCreds.host;
  }

  const creds = hit.credentials;
  // API keys first
  const priority = ['api_key', 'apiKey', 'key', 'secret_key', 'secretKey',
    'token', 'access_key', 'akia', 'accountSid', 'account_sid'];
  for (const k of priority) {
    if (creds[k] && typeof creds[k] === 'string') return creds[k];
  }
  // Try nested objects
  for (const v of Object.values(creds)) {
    if (v && typeof v === 'object') {
      for (const k of priority) {
        if ((v as any)[k] && typeof (v as any)[k] === 'string') return (v as any)[k];
      }
    }
  }
  // Fallback to first string
  for (const v of Object.values(creds)) {
    if (typeof v === 'string' && v.length > 0) return v;
  }
  return '—';
}

/* ─── Détails ────────────────────────────────────────────── */
function extractDetails(hit: Hit): string {
  const vr = hit.verificationResult;
  if (!vr) {
    if (hit.status === 'valid') return 'séparé — recheck…';
    return '—';
  }
  if (typeof vr === 'string') return vr;
  return vr.message || vr.error || vr.reason || vr.detail || JSON.stringify(vr).slice(0, 80);
}

/* ─── Scan path ──────────────────────────────────────────── */
function extractScan(hit: Hit): { name: string; file: string } {
  if (!hit.path) return { name: '—', file: '' };
  const parts = hit.path.split(/[/\\]/);
  const file = parts[parts.length - 1] || hit.path;
  const name = file.replace(/\.[^.]+$/, '');
  return { name, file };
}

/* ─── Heure relative ─────────────────────────────────────── */
function relTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}j`;
}

/* ─── Top service ────────────────────────────────────────── */
function topService(hits: Hit[]): { name: string; count: number } {
  const counts: Record<string, number> = {};
  hits.forEach(h => h.services?.forEach(s => { counts[s] = (counts[s] || 0) + 1; }));
  let top = { name: '—', count: 0 };
  for (const [name, count] of Object.entries(counts)) {
    if (count > top.count) top = { name, count };
  }
  return top;
}

/* ─── Service fields for expanded panel ──────────────────── */
interface FieldEntry { label: string; value: string; isKey?: boolean }

function getServiceFields(serviceKey: string, creds: any, vr: any): FieldEntry[] {
  const key = serviceKey.toLowerCase();
  const fields: FieldEntry[] = [];

  const add = (label: string, value: any, isKey = false) => {
    if (value !== undefined && value !== null && String(value).trim().length > 0) {
      fields.push({ label, value: String(value), isKey });
    }
  };

  if (key === 'smtp' || key === 'mail' || key === 'mailer') {
    if (isAwsRandom(creds)) {
      add('AKIA',           creds.username || creds.user, true);
      add('Secret Key',     creds.password || creds.pass || creds.smtp_password, true);
      add('Host (origine)', creds.host);
      add('Port',           creds.port || vr?.WorkingPort);
      add('Status',         vr?.Status);
      return fields.filter(f => f.value && f.value !== 'undefined');
    }
    add('Host',       creds.host, true);
    add('Port',       creds.port || vr?.WorkingPort);
    add('User',       creds.username || creds.user || creds.email);
    add('Mot de passe', creds.password || creds.pass || creds.smtp_password || creds.smtp_pass, true);
    add('From',       creds.from || creds.sender);
    add('Status',     vr?.Status);

  } else if (key === 'aws' || key === 'amazon') {
    add('AKIA',       creds.akia || creds.accessKeyId || creds.access_key, true);
    add('Secret Key', creds.secretKey || creds.secret_key || creds.secret, true);
    add('Region',     creds.region);
    if (vr?.ses) {
      const quota = vr.ses.Quota || vr.ses.SendingQuota;
      const sent  = vr.ses.SentLast24h ?? 0;
      if (quota > 0 || vr.ses.Status === 'Valid') add('SES', quota ? `${sent}/${quota} today` : 'Valid', true);
    }
    if (vr?.sns?.MonthlyLimit > 0 || vr?.sns?.Status === 'Valid')
      add('SNS', vr.sns.MonthlyLimit ? `Limit: ${vr.sns.MonthlyLimit}` : 'Valid', true);
    if (vr?.bedrock?.Status === 'Valid') add('Bedrock', 'Valid', true);
    if (vr?.s3?.Buckets > 0 || vr?.s3?.Status === 'Valid')
      add('S3', vr.s3.Buckets ? `${vr.s3.Buckets} buckets` : 'Valid', true);
    if (vr?.iam) {
      const perms: string[] = [];
      if (vr.iam.CanCreateUser) perms.push('CreateUser');
      if (vr.iam.IsRoot) perms.push('ROOT');
      if (perms.length) add('IAM', perms.join(' | '), true);
    }

  } else if (key === 'sendgrid') {
    add('API Key',  creds.apiKey || creds.api_key, true);
    add('From',     vr?.FromEmail || creds.from);
    if (vr?.IsUnlimited) add('Quota', 'Illimité', true);
    else if (vr?.RemainingQuota !== undefined) add('Quota', String(vr.RemainingQuota), true);
    add('Status', vr?.Status);

  } else if (key === 'brevo' || key === 'sendinblue') {
    add('API Key',       creds.apiKey || creds.api_key, true);
    add('From',          vr?.FromEmail || creds.from);
    if (vr?.EmailCredits > 0) add('Email Credits', String(vr.EmailCredits), true);
    if (vr?.SmsCredits   > 0) add('SMS Credits',   String(vr.SmsCredits),   true);
    add('Status', vr?.Status);

  } else if (key === 'mailgun') {
    add('API Key', creds.apiKey || creds.api_key, true);
    add('Domain',  creds.domain || vr?.Domains?.[0]);
    add('Status',  vr?.Status);

  } else if (key === 'mailjet') {
    add('API Key',    creds.apiKey || creds.api_key, true);
    add('Secret Key', creds.secretKey || creds.secret_key, true);
    add('From',       vr?.FromEmail || creds.from);
    add('Status',     vr?.Status);

  } else if (key === 'postmark') {
    add('API Key', creds.apiKey || creds.postmark?.apiKey, true);
    add('From',    creds.from || vr?.FromEmail);
    add('Status',  vr?.Status);

  } else if (key === 'sparkpost' || key === 'messagebird') {
    add('API Key', creds.apiKey || creds.api_key, true);
    add('From',    creds.from || vr?.FromEmail);
    add('Status',  vr?.Status);

  } else if (key === 'twilio') {
    add('Account SID', creds.accountSid || creds.account_sid, true);
    add('Auth Token',  creds.authToken  || creds.auth_token,  true);
    add('Status',      vr?.Status);
    if (vr?.AccountInfo) add('Info', vr.AccountInfo);

  } else if (key === 'stripe') {
    add('Secret Key', creds.secretKey || creds.secret_key, true);
    add('Pub Key',    creds.publishableKey || creds.publishable_key);
    if (vr?.Balance !== undefined) add('Balance', `${vr.Balance} ${vr.Currency || ''}`, true);
    add('Business', vr?.BusinessName);
    add('Country',  vr?.Country);

  } else if (
    key === 'openai' || key === 'anthropic' || key === 'gemini' ||
    key === 'cohere' || key === 'mistral' || key === 'ai' ||
    key.includes('gpt') || key.includes('claude')
  ) {
    add('API Key', creds.apiKey || creds.api_key, true);
    add('Status',  vr?.Status);
    if (vr?.Models) add('Models', String(vr.Models));

  } else {
    add('API Key',  creds.apiKey || creds.api_key, true);
    add('Host',     creds.host);
    add('User',     creds.username || creds.user);
    add('Password', creds.password || creds.pass || creds.secret, true);
    for (const [k, v] of Object.entries(creds)) {
      if (fields.length >= 5) break;
      if (typeof v === 'string' && v.length > 0 && !fields.find(f => f.value === v))
        fields.push({ label: k, value: v });
    }
  }

  return fields.filter(f => f.value && f.value !== 'undefined');
}

/* ─── Export CSV ─────────────────────────────────────────── */
function exportCSV(hits: Hit[]) {
  const rows = [
    ['statut', 'service', 'scan', 'url', 'valeur', 'détails', 'heure'].join(','),
    ...hits.map(h => [
      getDisplayStatus(h), (h.services ?? []).join(';'), h.path ?? '',
      h.url ?? '', extractCredValue(h), extractDetails(h), h.discoveredAt,
    ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')),
  ];
  const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
  const a = Object.assign(document.createElement('a'), { href: URL.createObjectURL(blob), download: `hits_${Date.now()}.csv` });
  a.click(); URL.revokeObjectURL(a.href);
}

function exportTXT(hits: Hit[]) {
  const lines = hits.map(h =>
    `[${getDisplayStatus(h).toUpperCase()}] ${h.url ?? ''} | ${(h.services ?? []).join(',')} | ${extractCredValue(h)}`
  );
  const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
  const a = Object.assign(document.createElement('a'), { href: URL.createObjectURL(blob), download: `hits_${Date.now()}.txt` });
  a.click(); URL.revokeObjectURL(a.href);
}

/* ════════════════════════════════════════════════════════════
   COMPOSANT
════════════════════════════════════════════════════════════ */
const MyHits: React.FC<MyHitsProps> = ({ vpsConfig, vpsList, setActiveTab }) => {
  const [hits, setHits]                   = useState<Hit[]>([]);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState<string | null>(null);
  const [filterStatus, setFilterStatus]   = useState<'all' | DisplayStatus>('all');
  const [filterService, setFilterService] = useState<string>('all');
  const [search, setSearch]               = useState('');
  const [page, setPage]                   = useState(0);
  const [recheckingIds, setRecheckingIds] = useState<Set<string>>(new Set());
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingAll, setDeletingAll]     = useState(false);
  const [expandedHitId, setExpandedHitId] = useState<string | null>(null);
  const [copiedKey, setCopiedKey]         = useState<string | null>(null);
  const pendingScrollRef                  = React.useRef<string | null>(null);

  /* ── Load — fetch depuis tous les VPS, merge + dedup ── */
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Build unified VPS list
      const allVps: { ip: string; port: string }[] = [];
      if (vpsList && vpsList.length > 0) {
        allVps.push(...vpsList);
      } else if (vpsConfig.ip) {
        allVps.push(vpsConfig);
      }
      if (allVps.length === 0) throw new Error('Aucun VPS configuré');

      const results = await Promise.allSettled(
        allVps.map(vps => {
          const ctrl = new AbortController();
          const timer = setTimeout(() => ctrl.abort(), 8000);
          return scannerFetch(vps.ip, vps.port, '/api/hits?type=all', { signal: ctrl.signal })
            .then(r => { clearTimeout(timer); return r.ok ? r.json().then((d: any) => ({ d, vps })) : Promise.resolve(null); })
            .catch(() => { clearTimeout(timer); return null; });
        })
      );

      const seenIds = new Set<string>();
      const merged: Hit[] = [];
      for (const r of results) {
        if (r.status !== 'fulfilled' || !r.value) continue;
        const { d, vps } = r.value as { d: any; vps: { ip: string; port: string } };
        const batch: Hit[] = Array.isArray(d) ? d : (d?.hits ?? []);
        for (const h of batch) {
          if (!seenIds.has(h.id)) {
            seenIds.add(h.id);
            merged.push({ ...h, _sourceVps: vps });
          }
        }
      }

      if (merged.length === 0 && results.every(r => r.status === 'rejected')) {
        throw new Error('Tous les VPS sont inaccessibles');
      }

      setHits(merged);
    } catch (err: any) {
      setError(err?.message ?? 'Erreur réseau');
    } finally {
      setLoading(false);
    }
  }, [vpsConfig, vpsList]);

  // Lit le pendingHitId depuis localStorage à l'ouverture
  useEffect(() => {
    const id = localStorage.getItem('pendingHitId');
    if (id) {
      localStorage.removeItem('pendingHitId');
      pendingScrollRef.current = id;
    }
  }, []);

  // Après chaque chargement, si un hit est en attente, l'expand et scroll vers lui
  useEffect(() => {
    const id = pendingScrollRef.current;
    if (!id || loading) return;
    pendingScrollRef.current = null;
    setExpandedHitId(id);
    setTimeout(() => {
      document.getElementById(`myhit-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 80);
  }, [loading]);

  useEffect(() => { load(); }, [load]);

  const allServices = useMemo(() => {
    const s = new Set<string>();
    hits.forEach(h => (h.services ?? []).forEach(sv => {
      const creds = h.credentials?.[sv] ?? h.credentials?.[sv.toLowerCase()];
      s.add(getEffectiveSvcName(sv, creds ?? h.credentials).toLowerCase());
    }));
    return Array.from(s).sort();
  }, [hits]);

  /* ── Stats ── */
  const stats = useMemo(() => {
    const valide           = hits.filter(h => getDisplayStatus(h) === 'valide').length;
    const brut             = hits.filter(h => getDisplayStatus(h) === 'brut').length;
    const invalide         = hits.filter(h => getDisplayStatus(h) === 'invalide').length;
    const brutInvalidCount = hits.filter(isBrutInvalid).length;
    const rate            = hits.length ? Math.round((valide / hits.length) * 100) : 0;
    const svcCount        = new Set(hits.flatMap(h => h.services ?? [])).size;
    const top             = topService(hits);
    return { total: hits.length, valide, brut, invalide, rate, svcCount, top, brutInvalidCount };
  }, [hits]);

  /* ── Filtrage ── */
  const filtered = useMemo(() => {
    let list = hits;
    if (filterStatus !== 'all') list = list.filter(h => getDisplayStatus(h) === filterStatus);
    if (filterService !== 'all') list = list.filter(h =>
      getDisplaySvcChips(h).some(chip => chip.label.toLowerCase() === filterService)
    );
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(h =>
        h.url?.toLowerCase().includes(q) ||
        h.path?.toLowerCase().includes(q) ||
        h.services?.some(s => s.toLowerCase().includes(q)) ||
        extractCredValue(h).toLowerCase().includes(q)
      );
    }
    return [...list].sort((a, b) => new Date(b.discoveredAt).getTime() - new Date(a.discoveredAt).getTime());
  }, [hits, filterStatus, filterService, search]);

  useEffect(() => { setPage(0); }, [filterStatus, filterService, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage   = Math.min(page, totalPages - 1);
  const pageHits   = filtered.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);

  /* ── Copy helper ── */
  const copyToClipboard = useCallback((text: string, key: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(k => k === key ? null : k), 1500);
    }).catch(() => toast.error('Copie impossible'));
  }, []);

  /* ── Recheck — fetch direct sur le VPS source du hit ── */
  const handleRecheck = async (e: React.MouseEvent, hit: Hit) => {
    e.stopPropagation();
    if (recheckingIds.has(hit.id)) return;
    setRecheckingIds(prev => new Set(prev).add(hit.id));
    const targetVps = hit._sourceVps ?? vpsConfig;
    try {
      const response = await scannerFetch(
        targetVps.ip, targetVps.port,
        `/api/hits/${encodeURIComponent(hit.id)}/recheck`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' } },
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || `Recheck erreur ${response.status}`);
      if (data.hit) {
        setHits(prev => {
          const idx = prev.findIndex(h => h.id === data.hit.id);
          if (idx === -1) return prev;
          const next = [...prev]; next[idx] = data.hit; return next;
        });
      }
      toast.success(data.message || (data.isValid ? 'Recheck OK' : 'Recheck — invalide'));
    } catch (err: any) {
      toast.error(err?.message ?? 'Erreur recheck');
    } finally {
      setRecheckingIds(prev => { const s = new Set(prev); s.delete(hit.id); return s; });
    }
  };

  /* ── Clear all ── */
  const handleDeleteAll = async () => {
    setDeletingAll(true);
    try {
      await apiService.clearAllHits();
      setHits([]);
      setShowDeleteModal(false);
      toast.success('Tous les hits supprimés');
    } catch {
      toast.error('Erreur suppression');
    } finally {
      setDeletingAll(false);
    }
  };

  const toggleExpand = (hitId: string) => {
    setExpandedHitId(prev => prev === hitId ? null : hitId);
  };

  /* ════════════════ EXPANDED PANEL ═══════════════ */
  const renderExpandedPanel = (hit: Hit) => {
    const entries = hit.credentials ? Object.entries(hit.credentials) : [];
    const isVerified = !!hit.verificationResult;

    return (
      <tr className="mh-exp-row">
        <td colSpan={9} className="mh-exp-cell">
          <div className="mh-exp-panel">

            <div className="mh-exp-panel-header">
              <span className="mh-exp-panel-label">CREDENTIALS</span>
              {isVerified && <span className="mh-exp-verified-badge">✓ API vérifié</span>}
              {!isVerified && hit.status === 'valid' && (
                <span className="mh-exp-brut-badge">⏳ Non vérifié</span>
              )}
            </div>

            {entries.length === 0 ? (
              <p className="mh-exp-empty">Pas de credentials disponibles</p>
            ) : (
              <div className="mh-exp-services">
                {entries.map(([serviceKey, creds]: [string, any]) => {
                  const fields = getServiceFields(serviceKey, creds, hit.verificationResult);
                  if (fields.length === 0) return null;
                  const awsRnd = isAwsRandom(creds);
                  const svcLabel = getEffectiveSvcName(serviceKey, creds);
                  return (
                    <div key={serviceKey} className="mh-exp-svc-group">
                      <div className="mh-exp-svc-label-row">
                        <span className="mh-exp-svc-label">{svcLabel}</span>
                        {awsRnd && <span className="mh-badge-aws-random">⚡ AWS RANDOM</span>}
                      </div>
                      <div className="mh-exp-fcards">
                        {fields.map(f => {
                          const fKey = `${hit.id}-${serviceKey}-${f.label}`;
                          const isDone = copiedKey === fKey;
                          return (
                            <div key={f.label} className={`mh-exp-fcard${f.isKey ? ' mh-exp-fcard--key' : ''}`}>
                              <div className="mh-exp-fcard-top">
                                <span className="mh-exp-fcard-label">{f.label}</span>
                                <button
                                  className={`mh-copy-btn${isDone ? ' mh-copy-btn--done' : ''}`}
                                  onClick={e => { e.stopPropagation(); copyToClipboard(f.value, fKey); }}
                                  title={`Copier ${f.label}`}
                                >
                                  {isDone ? <FaCheck size={8} /> : <FaCopy size={8} />}
                                </button>
                              </div>
                              <span className="mh-exp-fcard-value">{f.value}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </td>
      </tr>
    );
  };

  /* ════════════════ RENDER ════════════════ */
  return (
    <div className="mh-page">

      <div className="mh-glow-ambient" aria-hidden="true" />

      {/* ── Header ── */}
      <div className="mh-header">
        <div className="mh-header-left">
          <h1 className="mh-title">Mes hits</h1>
          <p className="mh-subtitle">
            tes propres hits (scans que tu as lancés)&nbsp;—&nbsp;
            <strong>{stats.total}</strong> en base
          </p>
        </div>
        <div className="mh-header-actions">
          <button className="mh-btn mh-btn--outline" onClick={() => exportCSV(filtered)} disabled={!filtered.length}>
            <span className="mh-btn-icon">↓</span> CSV
          </button>
          <button className="mh-btn mh-btn--outline" onClick={() => exportTXT(filtered)} disabled={!filtered.length}>
            TXT
          </button>
          <button
            className="mh-btn mh-btn--danger"
            onClick={() => setShowDeleteModal(true)}
            disabled={!hits.length || loading}
          >
            <span className="mh-btn-icon">🗑</span> Tout supprimer
          </button>
        </div>
      </div>

      {/* ── Stat cards ── */}
      <div className="mh-stats">
        <div className="mh-stat">
          <div className="mh-stat-label">TOTAL</div>
          <div className="mh-stat-value">{stats.total}</div>
          <div className="mh-stat-sub">{stats.svcCount} services distincts</div>
        </div>
        <div className="mh-stat mh-stat--accent">
          <div className="mh-stat-label">VALIDÉS</div>
          <div className="mh-stat-value mh-stat-value--valide">
            {stats.valide}
            {stats.rate > 0 && <span className="mh-rate-badge">{stats.rate}%</span>}
          </div>
          <div className="mh-stat-sub">crédentiels prouvés côté API</div>
        </div>
        <div className="mh-stat">
          <div className="mh-stat-label">BRUTS</div>
          <div className="mh-stat-value mh-stat-value--brut">{stats.brut}</div>
          <div className="mh-stat-sub">
            non testés / réseau
            {stats.brutInvalidCount > 0 && (
              <span className="mh-stat-timeout"> · {stats.brutInvalidCount} réseau/auth</span>
            )}
          </div>
        </div>
        <div className="mh-stat">
          <div className="mh-stat-label">INVALIDES</div>
          <div className="mh-stat-value mh-stat-value--invalide">{stats.invalide}</div>
          <div className="mh-stat-sub">rejetés par l'API cible</div>
        </div>
        <div className="mh-stat">
          <div className="mh-stat-label">TOP SERVICE</div>
          <div className="mh-stat-value">
            {stats.top.name !== '—'
              ? <span className="mh-top-svc-chip">{stats.top.name}</span>
              : '—'}
          </div>
          <div className="mh-stat-sub">{stats.top.count > 0 ? `${stats.top.count} hits` : '—'}</div>
        </div>
      </div>

      {/* ── Filtres ── */}
      <div className="mh-filters">
        <div className="mh-select-wrap">
          <select className="mh-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value as any)}>
            <option value="all">TOUS STATUTS</option>
            <option value="valide">VALIDE</option>
            <option value="brut">BRUT</option>
            <option value="invalide">INVALIDE</option>
          </select>
        </div>
        <div className="mh-select-wrap">
          <select className="mh-select" value={filterService} onChange={e => setFilterService(e.target.value)}>
            <option value="all">TOUS SERVICES</option>
            {allServices.map(s => <option key={s} value={s}>{s.toUpperCase()}</option>)}
          </select>
        </div>
        <input
          className="mh-search" type="text" placeholder="Rechercher…"
          value={search} onChange={e => setSearch(e.target.value)}
        />
        <button className="mh-refresh-btn" onClick={load} disabled={loading} title="Rafraîchir">
          <FaSyncAlt className={loading ? 'mh-spin' : ''} />
        </button>
        <span className="mh-count">{filtered.length} / {hits.length} hits</span>
      </div>

      {/* ── Table ── */}
      <div className="mh-table-wrap">
        {loading && !hits.length ? (
          <div className="mh-state">
            <span className="mh-state-icon mh-spin-lg">⟳</span>
            <span>Chargement des hits…</span>
          </div>
        ) : error ? (
          <div className="mh-state mh-state--err">
            <span className="mh-state-icon">⚠</span>
            <span>{error}</span>
          </div>
        ) : !filtered.length ? (
          <div className="mh-state">
            <span className="mh-state-icon">◎</span>
            <span>{hits.length ? 'Aucun résultat — modifie les filtres.' : 'Lance un scan pour générer des hits.'}</span>
          </div>
        ) : (
          <table className="mh-table">
            <thead>
              <tr>
                <th style={{ width: '1.5rem' }}></th>
                <th>STATUT</th>
                <th>SERVICE</th>
                <th>SCAN</th>
                <th>URL</th>
                <th>VALEUR</th>
                <th>DÉTAILS</th>
                <th>HEURE</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {pageHits.map(hit => {
                const ds       = getDisplayStatus(hit);
                const scan     = extractScan(hit);
                const cred     = extractCredValue(hit);
                const det      = extractDetails(hit);
                const expanded = expandedHitId === hit.id;
                return (
                  <React.Fragment key={hit.id}>
                    <tr
                      id={`myhit-${hit.id}`}
                      className={`mh-row mh-row--${ds}${expanded ? ' mh-row--expanded' : ''}`}
                      onClick={() => toggleExpand(hit.id)}
                    >
                      <td className="mh-chevron-cell">
                        <span className={`mh-chevron${expanded ? ' mh-chevron--open' : ''}`}>
                          {expanded ? <FaChevronDown size={9} /> : <FaChevronRight size={9} />}
                        </span>
                      </td>
                      <td>
                        <span className={`mh-badge mh-badge--${ds}`}>
                          <span className="mh-dot" />
                          {STATUS_LABEL[ds]}
                        </span>
                        {isSuperHit(hit) && (
                          <span className="mh-badge mh-badge--special" style={{ marginLeft: '4px' }}>Special</span>
                        )}
                      </td>
                      <td>
                        <div className="mh-svcs">
                          {getDisplaySvcChips(hit).slice(0, 2).map(chip => (
                            <span key={chip.key} className="mh-svc">{chip.label}</span>
                          ))}
                          {(hit.services?.length ?? 0) > 2 && (
                            <span className="mh-svc mh-svc--more">+{hit.services!.length - 2}</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="mh-scan">
                          <span className="mh-scan-name">{scan.name}</span>
                          {scan.file && scan.file !== scan.name && (
                            <span className="mh-scan-file">{scan.file}</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className="mh-url" title={hit.url}>{hit.url ?? '—'}</span>
                      </td>
                      <td>
                        <span className="mh-cred" title={cred}>{cred}</span>
                      </td>
                      <td>
                        <span className="mh-detail" title={det}>{det}</span>
                      </td>
                      <td>
                        <span className="mh-time">{relTime(hit.discoveredAt)}</span>
                        <span className="mh-xp-badge">+{calcHitXP(hit)} XP</span>
                      </td>
                      <td>
                        <button
                          className={`mh-recheck${recheckingIds.has(hit.id) ? ' mh-recheck--spin' : ''}`}
                          onClick={(e) => handleRecheck(e, hit)}
                          disabled={recheckingIds.has(hit.id)}
                          title="Recheck complet des credentials"
                        >
                          <FaSyncAlt />
                        </button>
                      </td>
                    </tr>
                    {expanded && renderExpandedPanel(hit)}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Pagination ── */}
      {!loading && !error && filtered.length > PAGE_SIZE && (
        <div className="mh-pagination">
          <span className="mh-page-info">
            {safePage * PAGE_SIZE + 1}–{Math.min((safePage + 1) * PAGE_SIZE, filtered.length)} sur {filtered.length}
          </span>
          <div className="mh-page-btns">
            <button className="mh-page-btn" onClick={() => setPage(0)} disabled={safePage === 0}>«</button>
            <button className="mh-page-btn" onClick={() => setPage(p => p - 1)} disabled={safePage === 0}>‹</button>
            {Array.from({ length: totalPages }, (_, i) => i)
              .filter(i => Math.abs(i - safePage) <= 2)
              .map(i => (
                <button key={i} className={`mh-page-btn${i === safePage ? ' mh-page-btn--active' : ''}`} onClick={() => setPage(i)}>
                  {i + 1}
                </button>
              ))}
            <button className="mh-page-btn" onClick={() => setPage(p => p + 1)} disabled={safePage >= totalPages - 1}>›</button>
            <button className="mh-page-btn" onClick={() => setPage(totalPages - 1)} disabled={safePage >= totalPages - 1}>»</button>
          </div>
        </div>
      )}

      {/* ── Modal suppression ── */}
      {showDeleteModal && (
        <div className="mh-overlay" onClick={() => !deletingAll && setShowDeleteModal(false)}>
          <div className="mh-modal" onClick={e => e.stopPropagation()}>
            <p className="mh-modal-title">Supprimer tous les hits ?</p>
            <p className="mh-modal-body">
              Cette action supprime définitivement <strong>{hits.length} hits</strong> de la base. Elle est irréversible.
            </p>
            <div className="mh-modal-actions">
              <button className="mh-btn mh-btn--outline" onClick={() => setShowDeleteModal(false)} disabled={deletingAll}>
                Annuler
              </button>
              <button className="mh-btn mh-btn--danger" onClick={handleDeleteAll} disabled={deletingAll}>
                {deletingAll ? 'Suppression…' : 'Confirmer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyHits;
